import { create } from 'zustand';
import type { DIDLocator } from '../types';

interface OrderDIDsState {
  // Search filters
  state: string;
  areaCode: string;
  rateCenter: string;
  numberType: 'local' | 'toll-free';

  // Search results
  results: DIDLocator[];
  totalResults: number;
  isSearching: boolean;
  hasSearched: boolean;

  // Selection state
  selectedDIDs: Set<string>;

  // Modal state
  isConfirmModalOpen: boolean;

  // Actions
  setFilters: (filters: Partial<Pick<OrderDIDsState, 'state' | 'areaCode' | 'rateCenter' | 'numberType'>>) => void;
  resetFilters: () => void;

  setResults: (results: DIDLocator[], total: number) => void;
  clearResults: () => void;
  setIsSearching: (isSearching: boolean) => void;

  selectDID: (tn: string) => void;
  deselectDID: (tn: string) => void;
  toggleDID: (tn: string) => void;
  selectAll: (tns: string[]) => void;
  clearSelection: () => void;

  openConfirmModal: () => void;
  closeConfirmModal: () => void;

  // Remove ordered DIDs from results
  removeOrderedDIDs: (tns: string[]) => void;
}

export const useOrderDIDsStore = create<OrderDIDsState>((set) => ({
  // Initial state
  state: '',
  areaCode: '',
  rateCenter: '',
  numberType: 'local',

  results: [],
  totalResults: 0,
  isSearching: false,
  hasSearched: false,

  selectedDIDs: new Set(),

  isConfirmModalOpen: false,

  // Actions
  setFilters: (filters) =>
    set((state) => ({
      ...state,
      ...filters,
      // Clear dependent fields when state changes
      ...(filters.state !== undefined && filters.state !== state.state
        ? { areaCode: '', rateCenter: '' }
        : {}),
    })),

  resetFilters: () =>
    set({
      state: '',
      areaCode: '',
      rateCenter: '',
      numberType: 'local',
      results: [],
      totalResults: 0,
      hasSearched: false,
      selectedDIDs: new Set(),
    }),

  setResults: (results, total) =>
    set({
      results,
      totalResults: total,
      hasSearched: true,
      isSearching: false,
      selectedDIDs: new Set(),
    }),

  clearResults: () =>
    set({
      results: [],
      totalResults: 0,
      hasSearched: false,
      selectedDIDs: new Set(),
    }),

  setIsSearching: (isSearching) => set({ isSearching }),

  selectDID: (tn) =>
    set((state) => ({
      selectedDIDs: new Set([...state.selectedDIDs, tn]),
    })),

  deselectDID: (tn) =>
    set((state) => {
      const next = new Set(state.selectedDIDs);
      next.delete(tn);
      return { selectedDIDs: next };
    }),

  toggleDID: (tn) =>
    set((state) => {
      const next = new Set(state.selectedDIDs);
      if (next.has(tn)) {
        next.delete(tn);
      } else {
        next.add(tn);
      }
      return { selectedDIDs: next };
    }),

  selectAll: (tns) =>
    set({
      selectedDIDs: new Set(tns),
    }),

  clearSelection: () =>
    set({
      selectedDIDs: new Set(),
    }),

  openConfirmModal: () => set({ isConfirmModalOpen: true }),

  closeConfirmModal: () => set({ isConfirmModalOpen: false }),

  removeOrderedDIDs: (tns) =>
    set((state) => {
      const orderedSet = new Set(tns);
      return {
        results: state.results.filter((did) => !orderedSet.has(did.tn)),
        totalResults: state.totalResults - tns.length,
        selectedDIDs: new Set(),
      };
    }),
}));
